import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  inject,
  isDevMode,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { FilenameConventionEvBased } from 'src/app/models/filename-conventions/filename-convention-payload';
import {
  EnhancementVariantDetail,
  IMasterSuite,
} from 'src/app/models/mastersuites/mastersuite';
import { EnhancementVariantListResult } from 'src/app/models/mastersuites/mastersuite-result';

import { EvConventionEditorPayload } from '../ev-convention-editor/ev-convention-editor.component';
import { evEditorPayloadToConvention } from '../ev-convention-payload.adapter';

export interface EvFilenameConventionCreateDialogData {
  organizationId: string;
}

@Component({
  selector: 'app-ev-filename-convention-create-dialog',
  templateUrl: './ev-filename-convention-create-dialog.component.html',
  styleUrls: ['./ev-filename-convention-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EvFilenameConventionCreateDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly familyBadge = 'EV';

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  submitting = false;
  evPayload: EvConventionEditorPayload | null = null;

  masterSuites: readonly IMasterSuite[] = [];
  enhancementVariants: readonly EnhancementVariantDetail[] = [];
  configuredEvIds: readonly string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EvFilenameConventionCreateDialogData,
    private readonly dialogRef: MatDialogRef<
      EvFilenameConventionCreateDialogComponent,
      FilenameConvention | undefined
    >,
    private readonly service: FilenameConventionService,
    private readonly masterSuitesService: MasterSuitesService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadConfiguredEvIds();
    this.loadMasterSuitesAndEvs();
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.evPayload === null) return false;
    if (this.evPayload.conventionMsEv == null) return false;
    return true;
  }

  onEvEditorChange(payload: EvConventionEditorPayload): void {
    if (isDevMode()) {
      console.log('[EV create dialog] onEvEditorChange', {
        conventionMsEv: payload.conventionMsEv,
        hasField: 'conventionMsEv' in payload,
        type: typeof payload.conventionMsEv,
      });
    }
    this.evPayload = payload;
  }

  submit(): void {
    if (!this.canSubmit) return;
    if (this.evPayload === null) return;
    if (isDevMode()) {
      console.log('[EV create dialog] submit', {
        evPayload: this.evPayload,
        conventionMsEv: this.evPayload.conventionMsEv,
        hasField: 'conventionMsEv' in this.evPayload,
      });
    }
    const payload = evEditorPayloadToConvention(this.evPayload);
    if (payload === null) return;
    this.submitting = true;
    this.service
      .create(this.data.organizationId, {
        name: this.nameControl.value.trim(),
        value: { payload },
        writeReason: { kind: 'initialCreate' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FilenameConvention) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private loadConfiguredEvIds(): void {
    this.service
      .list(this.data.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((conventions) => {
        this.configuredEvIds = this.collectConfiguredEvIds(conventions);
        this.cdr.markForCheck();
      });
  }

  private loadMasterSuitesAndEvs(): void {
    this.masterSuitesService
      .listMasterSuites(this.data.organizationId)
      .pipe(
        switchMap((suites: IMasterSuite[]) => {
          this.masterSuites = suites;
          const suitesWithId = suites.filter((ms): ms is IMasterSuite & { id: string } => ms.id !== null);
          if (suitesWithId.length === 0) return of([] as EnhancementVariantDetail[]);
          return forkJoin(
            suitesWithId.map((ms) =>
              this.masterSuitesService
                .getAllEnhancementVariantOfMasterSuite(ms.id)
                .pipe(map((res: EnhancementVariantListResult) => res.enhancementVariants ?? [])),
            ),
          ).pipe(map((lists) => lists.flat()));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((all: EnhancementVariantDetail[]) => {
        this.enhancementVariants = all;
        this.cdr.markForCheck();
      });
  }

  private collectConfiguredEvIds(conventions: readonly FilenameConvention[]): string[] {
    return conventions
      .filter((c) => c.value.payload.mode === 'evBased')
      .flatMap((c) => (c.value.payload as FilenameConventionEvBased).entries)
      .map((entry) => entry.masterSuiteEnhancementVariantId);
  }
}
